import { Box, Typography } from "@mui/material";
import { optionDescription, optionLabel } from "./Step2.rules";

export { renderOptionValue } from "./Step2.rules";

type Option = { key: string; label: string; description?: string };

/**
 * Options that explain themselves while the list is open (S2.13, story 5623, task 5729).
 *
 * Every step-2 option already carries a one-line plain-English wording in reference data, and each
 * field showed it — but only for the value already chosen, as a caption under the closed control.
 * The open list showed bare labels, so the explanation was missing at the one moment it is needed:
 * while someone is choosing, with a client reading over their shoulder. That is the whole point of
 * S2.13's "inline, not hover-only" — a tooltip is invisible on a tablet, and so is a caption that
 * only describes the option you have already committed to.
 *
 * Two halves, and both are needed:
 *
 *   - `optionMenuContent` renders the label with its wording underneath, inside the MenuItem.
 *   - `renderOptionValue` keeps the *closed* control to one line.
 *
 * Without the second, MUI's Select would echo the whole two-line block into the closed field, which
 * would wreck the occasion tables — their selects sit in 150px cells.
 */
export const optionMenuContent = (
  options: Option[] | undefined,
  key: string,
  labelOverride?: string,
) => {
  const description = optionDescription(options, key);
  return (
    <Box sx={{ py: 0.25 }}>
      <Typography variant="body2" component="div">
        {labelOverride ?? optionLabel(options, key)}
      </Typography>
      {description && (
        // MenuItem sets white-space: nowrap, which a description of this length has to override, and
        // the width cap stops one long wording stretching the menu across the screen.
        <Typography
          variant="caption"
          color="text.secondary"
          component="div"
          sx={{ whiteSpace: "normal", maxWidth: 360, lineHeight: 1.35 }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

